import React, { useState } from "react";
import { ReviewBonusSubmissionInputValues } from "components/ReviewBonusSubmissionInput";
import { ReviewARSubmissionInputValues } from "components/ReviewARSubmissionInput";
import { Button } from "@wellth/web-ui";
import { Input, Switch } from "antd";
import ReviewSubmissionCard, {
  ReviewCardProps,
  BonusBehaviorSubmissionRecord,
} from "./components/ReviewCard";

const HeaderStyle: React.CSSProperties = {
  marginLeft: "1.25rem",
  fontWeight: "bold",
  fontSize: "2.125rem",
};

const CenterFeedStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
};

const ControlsStyle: React.CSSProperties = {
  alignSelf: "center",
  margin: "1.25rem",
  display: "flex",
  flexDirection: "row",
  width: "70rem",
  justifyContent: "space-between",
  gap: "1rem",
};

const LoadMoreStyle: React.CSSProperties = {
  width: "6.25rem",
  alignSelf: "center",
  margin: "1.25rem",
};

type BonsuBehaviorSubmissionNode = BonusBehaviorSubmissionRecord & {
  memberId: string;
};

interface BonusBehaviorSubmissionEdge {
  node: BonsuBehaviorSubmissionNode;
  cursor: string;
}

export interface ManageBonusBehaviorSubmissionsProps
  extends Pick<ReviewCardProps, "onError"> {
  submissions: BonusBehaviorSubmissionEdge[];
  disableLoadMore: boolean;
  loadMore: () => void;
  submitSubmissionReview: (
    review: ReviewBonusSubmissionInputValues & { id: string },
  ) => Promise<void>;
  submitARSubmissionReview: (
    review: ReviewARSubmissionInputValues & { id: string },
  ) => Promise<void>;
  routeToMember: (id: string) => void;
}

const asc = (a: BonusBehaviorSubmissionEdge, b: BonusBehaviorSubmissionEdge) =>
  Date.parse(b.node.timestamp) - Date.parse(a.node.timestamp);
const desc = (a: BonusBehaviorSubmissionEdge, b: BonusBehaviorSubmissionEdge) =>
  Date.parse(a.node.timestamp) - Date.parse(b.node.timestamp);

function matchesFilter(value: unknown, filter: string): boolean {
  if (!filter) return true;
  const stringValue = typeof value === "string" ? value : value?.toString() ?? "";
  return stringValue.toLowerCase().startsWith(filter.toLowerCase());
}

export const ManageBonusBehaviorSubmissions: React.FC<
  ManageBonusBehaviorSubmissionsProps
> = ({
  submissions,
  loadMore,
  onError,
  submitSubmissionReview,
  submitARSubmissionReview,
  disableLoadMore,
  routeToMember,
}) => {
  const [isSortAsc, setIsSortAsc] = useState(true);
  const [programFilter, setProgramFilter] = useState("");
  const [shortIdFilter, setShortIdFilter] = useState("");
  const [nameFilter, setNameFilter] = useState("");
  const [showCount, setShowCount] = useState(10);
  const sortedSubmissions = [...submissions].sort(isSortAsc ? asc : desc);
  const filteredList = sortedSubmissions
    .filter((s) =>
      matchesFilter(s.node.programCode, programFilter) &&
      matchesFilter(s.node.shortId, shortIdFilter) &&
      matchesFilter(s.node.name, nameFilter)
    )
    .slice(0, showCount);

  function handleLoadMore() {
    if (showCount < sortedSubmissions.length) {
      setShowCount(showCount + 10);
      return;
    }

    loadMore();
  }

  return (
    <>
      <h1 style={HeaderStyle}>Manage Bonus Submissions</h1>

      <div style={CenterFeedStyle}>
        <div style={ControlsStyle}>
          <Input
            style={{ width: "20rem" }}
            placeholder="Program Code Filter"
            value={programFilter}
            onChange={(e) => setProgramFilter(e.target.value)}
            allowClear
          />
          <Input
            style={{ width: "20rem" }}
            placeholder="Short ID Filter"
            value={shortIdFilter}
            onChange={(e) => setShortIdFilter(e.target.value)}
            allowClear
          />
          <Input
            style={{ width: "20rem" }}
            placeholder="Behavior Type Filter"
            value={nameFilter}
            onChange={(e) => setNameFilter(e.target.value)}
            allowClear
          />
          <span style={{ width: "20rem" }}>
            Order submissions by date{" "}
            <Switch
              defaultChecked
              onChange={setIsSortAsc}
              checkedChildren="DESC"
              unCheckedChildren="ASC"
            />
          </span>
        </div>
        {filteredList.map(
          ({
            node: {
              id,
              name,
              status,
              shortId,
              memberId,
              timestamp,
              firstName,
              lastName,
              programCode,
              rewardAmount,
              rewardCurrency,
              photoUrl,
              photoUrls,
              verification,
            },
          }) => (
            <ReviewSubmissionCard
              key={`${id}-card`}
              id={id}
              name={name}
              status={status}
              shortId={shortId}
              timestamp={timestamp}
              firstName={firstName}
              lastName={lastName}
              programCode={programCode}
              rewardAmount={rewardAmount}
              rewardCurrency={rewardCurrency}
              photoUrl={photoUrl}
              photoUrls={photoUrls}
              verification={verification}
              routeToMemberPage={() => window.open(`/member/${memberId}`, '_blank', 'noopener,noreferrer')}
              onError={({ message }): void =>
                onError(new Error(`${message} for id: ${id}`))
              }
              submit={(review): Promise<void> =>
                submitSubmissionReview({ ...review, id })
              }
              submitAR={(review): Promise<void> =>
                submitARSubmissionReview({ ...review, id })
              }
            />
          ),
        )}
        {filteredList.length > 1 && (
          <Button
            type="primary"
            style={LoadMoreStyle}
            onClick={handleLoadMore}
            disabled={disableLoadMore && showCount >= sortedSubmissions.length}
          >
            Load More
          </Button>
        )}
      </div>
    </>
  );
};

export default ManageBonusBehaviorSubmissions;
